import _ from 'lodash';
import memoize from 'memoizee';

import ToastActions from 'actions/ToastActions';
// @ts-ignore
import { FIRST_PAGE_VAR } from 'chat_v2/components/ChatStream/components/ChatStreamContainer';
import {
  ActionTypes,
  CHATS_PAGE_SIZE,
  DATA_RELOAD_TIMEOUT_MS,
  SEARCH_NETWORK_LIMIT_MS,
} from 'chat_v2/constants/ChatConstants';
import Chats from 'collections/Chats';
// @ts-ignore
import PotentialChatMembers from 'collections/PotentialChatMembers';
import Chat from 'entities/Chat';
import ChatMembership from 'entities/ChatMembership';
import ChatMessage from 'entities/ChatMessage';
import Group from 'entities/Group';
// @ts-ignore
import PotentialChatMember, { PotentialOrRegularChatMember } from 'entities/PotentialChatMember';
import { rootGroup } from 'entities/RootGroup';
import { dispatcher, reduxDispatcher } from 'lib/Dispatcher';
import API from 'lib/RemindAPI';
import errorLogger from 'utils/errorLogger';

import { LoadChats, LoadChats_chatStreams } from 'chat/queries/__generated__/LoadChats';
import { Backbone$Id } from 'types/typescript/Backbone';

import LOAD_CHATS_QUERY from 'chat/queries/LoadChatsQuery.gql';

type LoadedRange = {
  oldestUpdate?: Date | null;
  newestUpdate?: Date | null;
};

function transformChat(chatStream: LoadChats_chatStreams) {
  const { permissions } = chatStream;
  return {
    ...chatStream,
    has_muted_members: chatStream.hasMutedMembers,
    maximum_members: chatStream.maximumMembers,
    memberships_count: chatStream.membershipsCount,
    other_member: chatStream.otherMember,
    query_key: chatStream.queryKey,
    unread_messages: chatStream.unreadMessagesCount,
    memberships: chatStream.memberships.nodes,
    can_add: permissions.canAdd,
    can_send: permissions.canSend,
    can_open: permissions.canOpen,
    can_phone: permissions.canPhone,
    can_phone_denial_reason: permissions.canPhoneDenialReason,
    can_manage_replies: permissions.canManageReplies,
    can_leave: permissions.canLeave,
  };
}
type TransformedChat = ReturnType<typeof transformChat>;

async function loadSpecificChat(
  chatUuid: string,
  forceFetch: boolean
): Promise<TransformedChat | {}> {
  const variables = { chatUuids: [chatUuid], ...FIRST_PAGE_VAR };
  const data = await API.execute<LoadChats>({ query: LOAD_CHATS_QUERY, variables, forceFetch });
  const chatStream = data.chatStreams && data.chatStreams[0];

  return chatStream ? transformChat(chatStream) : {};
}

async function loadChatsForGroup(
  groupId: number,
  maxTimestamp: string | undefined | null,
  lastUpdatedTimestamp: string | undefined | null,
  forceFetch: boolean
): Promise<TransformedChat[]> {
  const variables = { groupId, maxTimestamp, lastUpdatedTimestamp };
  const data = await API.execute<LoadChats>({ query: LOAD_CHATS_QUERY, variables, forceFetch });
  const { chatStreams } = data;

  if (!chatStreams) {
    return [];
  }

  return chatStreams.map(chatStream => transformChat(chatStream));
}

type LoadedChatRangeMapMapType = { [key: string]: LoadedRange };

const loadedChatRangeMap: {
  map: LoadedChatRangeMapMapType;
  getLoadedChatRangeForGroup(groupId: Backbone$Id): LoadedRange;
  setLoadedChatRangeForGroup(groupId: Backbone$Id, chats: Chats): void;
} = {
  map: {},
  getLoadedChatRangeForGroup(groupId: Backbone$Id): LoadedRange {
    // eslint-disable-line
    if (!this.map[groupId]) {
      this.map[groupId] = {
        oldestUpdate: null,
        newestUpdate: null,
      };
    }

    return this.map[groupId] as LoadedRange;
  },
  setLoadedChatRangeForGroup(groupId: Backbone$Id, chats: Chats): void {
    // eslint-disable-line
    if (chats.isEmpty()) {
      return;
    }

    const existing = this.getLoadedChatRangeForGroup(groupId);

    const oldestUpdate = chats.getOldestUpdatedAt();
    if (oldestUpdate && (!existing.oldestUpdate || oldestUpdate < existing.oldestUpdate)) {
      existing.oldestUpdate = oldestUpdate;
    }

    const newestUpdate = chats.getNewestUpdatedAt();
    if (newestUpdate && (!existing.newestUpdate || newestUpdate > existing.newestUpdate)) {
      existing.newestUpdate = newestUpdate;
    }
  },
};

const ChatActions = {
  createChat(chatToInitiate: Chat, message?: ChatMessage): Promise<Chat> {
    return chatToInitiate.initiate(message).then(chat => {
      dispatcher.dispatch({
        type: ActionTypes.CREATE_CHAT,
        chat,
      });

      // set the chat id on the message.
      // this is necessary because the created chat may
      // not have the same uuid as the chat given in the parameters
      // if there was a preexisting one to one chat.
      if (message) {
        message.setChatId(chat.id);

        dispatcher.dispatch({
          type: ActionTypes.SENT_MESSAGE,
          message,
        });
      }
      return chat;
    });
  },

  resetStore(): void {
    dispatcher.dispatch({
      type: ActionTypes.RESET_STORE,
    });
  },

  changeMemberStatus(chat: Chat, membersId: Backbone$Id, state: string): Promise<ChatMembership> {
    return chat.changeMemberStatus(membersId, state).then(membership => {
      dispatcher.dispatch({
        type: ActionTypes.EDIT_MEMBER_FROM_CHAT,
        membership,
        chat,
      });

      return membership;
    });
  },

  addMembersToChat(chat: Chat, membersIds: Array<Backbone$Id>) {
    return chat.addMembers(membersIds).then(memberships => {
      dispatcher.dispatch({
        type: ActionTypes.ADD_MEMBERS_TO_CHAT,
        memberships,
        chat,
      });

      return memberships;
    });
  },

  loadChats: (group: Group): void => {
    // This promise is memoized so that this method doesn't hit the network
    // too frequently.
    ChatActions.promiseLoadChats(group);
  },

  promiseLoadChats: memoize(
    (group: { id: string }): Promise<Chats | void> => {
      const groupId = group.id;
      return Chats.loadForGroup(groupId)
        .then(chats => {
          loadedChatRangeMap.setLoadedChatRangeForGroup(groupId, chats);

          reduxDispatcher.dispatch({
            type: 'chats/LIST_CHATS',
            chats,
          });

          dispatcher.dispatch({
            type: ActionTypes.LIST_CHATS,
            groupId,
            chats,
          });
          return chats as Chats;
        })
        .catch(err => {
          ToastActions.showErrorToast(
            'Messages could not be loaded. Please wait and try again later.'
          );
          console.warn(err, err && err.stack);
        });
    },
    {
      promise: true,
      maxAge: DATA_RELOAD_TIMEOUT_MS,
    }
  ),

  loadMoreChats(group: Group): void {
    const { oldestUpdate, newestUpdate } = loadedChatRangeMap.getLoadedChatRangeForGroup(group.id);

    ChatActions.loadChatPage(
      group.id,
      oldestUpdate ? oldestUpdate.toISOString() : null,
      newestUpdate ? newestUpdate.toISOString() : null
    );
  },

  loadChatPage: memoize(
    async (
      groupId: number,
      updatedBefore?: string | null,
      lastUpdated?: string | null
    ): Promise<void> => {
      const data: any = {};
      if (updatedBefore) {
        data['max_ts'] = updatedBefore;
      }
      if (lastUpdated) {
        data['last_updated_ts'] = lastUpdated;
      }
      if (groupId !== rootGroup.id) {
        data['group_id'] = groupId;
      }
      data.limit = CHATS_PAGE_SIZE;
      // postpone loading this until a specific chat view is requesting it
      data.include_memberships = false;

      const params = {
        data,
      };

      const chatsArray = await loadChatsForGroup(groupId, updatedBefore, lastUpdated, true);
      // chatsArray still in GQL form but has all the necessary parts of the model
      const chats = Chats.fromJSON(chatsArray as []) as Chats;

      loadedChatRangeMap.setLoadedChatRangeForGroup(groupId, chats);
      reduxDispatcher.dispatch({
        type: 'chats/APPEND_CHATS',
        chats,
      });
      dispatcher.dispatch({
        groupId,
        type: ActionTypes.APPEND_CHATS,
        chats,
        ...params,
      });
    }
  ),

  searchChats: _.debounce(
    memoize(
      (query: any) => {
        Chats.search(query)
          .then(chats => {
            dispatcher.dispatch({
              type: ActionTypes.SEARCH_CHATS,
              chats,
            });
          })
          .catch(errorLogger('Chats.search failed'));
      },
      {
        maxAge: DATA_RELOAD_TIMEOUT_MS,
      }
    ),
    SEARCH_NETWORK_LIMIT_MS
  ),

  resetSelection(): void {
    dispatcher.dispatch({
      type: ActionTypes.RESET_SELECTION,
    });
  },

  selectChat(chatId: Backbone$Id): void {
    dispatcher.dispatch({
      type: ActionTypes.SELECT_CHAT,
      chatId,
    });
  },

  loadLocal(
    members: Array<PotentialOrRegularChatMember>,
    title: string,
    originIds: Backbone$Id[] = []
  ): void {
    dispatcher.dispatch({
      type: ActionTypes.LOAD_LOCAL_CHAT,
      members,
      title,
      originIds,
    });
  },

  updateLocal(): void {
    dispatcher.dispatch({
      type: ActionTypes.UPDATE_LOCAL_CHAT,
    });
  },

  destroyLocal(): void {
    dispatcher.dispatch({
      type: ActionTypes.DESTROY_LOCAL_CHAT,
    });
  },

  async loadChat(chatUuid: string): Promise<Chat> {
    const chatJSON = await loadSpecificChat(chatUuid, true);
    const chat = Chat.fromJSON(chatJSON);

    reduxDispatcher.dispatch({
      type: 'chats/LOAD_CHAT',
      chat,
    });
    dispatcher.dispatch({
      type: ActionTypes.LOAD_CHAT,
      chat,
    });

    return chat as Chat;
  },

  getChatFromChatStream(chatStream: LoadChats_chatStreams) {
    return Chat.fromJSON(transformChat(chatStream)) as Chat;
  },

  markRead(chatId: Backbone$Id): void {
    try {
      dispatcher.dispatch({
        type: ActionTypes.MARK_READ,
        chatId,
      });
    } catch (e) {
      console.warn(e, e && e.stack);
      console.error('Chat.markRead failed');
    }
  },

  markReadUpto(chatId: Backbone$Id, seqId: number): void {
    ChatActions.markRead(chatId);
    Chat.markReadUpto(chatId, seqId)
      .then(
        () => {
          dispatcher.dispatch({
            type: ActionTypes.READ_MESSAGE,
            chatId,
          });
        },
        err => {
          console.warn({ err, chatId, seqId });
          console.error('Chat.markReadUpTo request failed');
        }
      )
      .catch(errorLogger('Chat.markReadUpTo runtime error'));
  },

  disableReplies(chatToDisable: Chat): void {
    chatToDisable
      .disableReplies()
      .then(chat => {
        ToastActions.showSuccessToast('Replies are turned off');
        dispatcher.dispatch({
          type: ActionTypes.UPDATE_CHAT,
          chat,
        });
      })
      .catch(errorLogger('Chat.disableReplies failed'));
  },

  enableReplies(chatToEnable: Chat): void {
    chatToEnable
      .enableReplies()
      .then(chat => {
        ToastActions.showSuccessToast('Replies are turned on');
        dispatcher.dispatch({
          type: ActionTypes.UPDATE_CHAT,
          chat,
        });
      })
      .catch(errorLogger('Chat.enableReplies failed'));
  },

  leaveChat(chat: Chat): void {
    chat
      .leave()
      .then(() => {
        dispatcher.dispatch({
          type: ActionTypes.UPDATE_CHAT,
          chat,
        });
      })
      .catch(errorLogger('Chat.leave failed'));
  },

  updateChatTitle(chat: Chat, title: string): void {
    chat
      .updateTitle(title)
      .then(() => {
        dispatcher.dispatch({
          type: ActionTypes.UPDATE_CHAT,
          chat,
        });
      })
      .catch(errorLogger('Chat.updateTitle failed'));
  },

  // TODO remove because each PCM query is unique. It's not just loading data
  // into a store, the order of the results is significant.
  loadPotentialChatMembers: _.debounce(function(query: string) {
    ChatActions.promisePotentialChatMembers(query);
  }, SEARCH_NETWORK_LIMIT_MS),

  promisePotentialChatMembers: memoize(
    (query: any) => {
      return PotentialChatMembers.fetch({
        data: {
          q: query,
        },
      }).then((members: any) => {
        dispatcher.dispatch({
          type: ActionTypes.LOAD_MEMBERS,
          members,
          query,
        });
        return members;
      });
    },
    {
      maxAge: DATA_RELOAD_TIMEOUT_MS,
    }
  ),

  emailTranscript(chatId: Backbone$Id): void {
    Chat.emailTranscript(chatId)
      .then(() => {
        ToastActions.showSuccessToast('We’ve sent the message history for this conversation.');
      })
      .catch(error => {
        ToastActions.showAPIError(error);
        throw error;
      });
  },
  reportChat(chatId: Backbone$Id, uuid: string, key: string, notes?: string): void {
    Chat.reportChat(chatId, uuid, key, notes)
      .then(() => {
        ToastActions.showSuccessToast('Report submitted');
      })
      .catch(errorLogger('Chat.reportChat failed'));
  },
  requestMemberAge(memberToRequest: PotentialChatMember): void {
    memberToRequest
      .requestAge()
      .then((member: PotentialChatMember) => {
        dispatcher.dispatch({
          type: ActionTypes.UPDATE_MEMBER,
          member,
        });
      })
      .catch(errorLogger('memberToRequest.requestAge failed'));
  },
  setFocusView(focus: number): void {
    dispatcher.dispatch({
      type: ActionTypes.SET_FOCUS_VIEW,
      focus,
    });
  },
  enableRepliesForAllChats(): Promise<void> {
    return Chats.enableRepliesForAllChats();
  },
  disableRepliesForAllChats(): Promise<void> {
    return Chats.disableRepliesForAllChats();
  },
  resetPromiseMemoization(): void {
    ChatActions.promiseLoadChats.clear();
    ChatActions.promisePotentialChatMembers.clear();
    loadedChatRangeMap.map = {};
  },
};
export default ChatActions;
